const ensureGoogle = () => {
  if (!window.google || !window.google.maps) {
    throw new Error("Google Maps JS API not loaded");
  }
};

export const getPlacePredictions = (input) => {
  return new Promise((resolve) => {
    try {
      ensureGoogle();

      const service = new window.google.maps.places.AutocompleteService();

      service.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: "in" },
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            resolve(predictions);
          } else {
            resolve([]);
          }
        }
      );
    } catch (err) {
      console.error(err);
      resolve([]);
    }
  });
};

export const getPlaceDetails = (placeId) => {
  return new Promise((resolve, reject) => {
    try {
      ensureGoogle();

      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );

      service.getDetails(
        {
          placeId,
          fields: ["name", "formatted_address", "geometry", "address_components"],
        },
        (result, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
            reject(new Error(`Places API error: ${status}`));
            return;
          }

          if (!result) {
            reject(new Error('No result found for this place'));
            return;
          }
          const components = {};
          if (result.address_components && Array.isArray(result.address_components)) {
            result.address_components.forEach((c) => {
              c.types.forEach((t) => (components[t] = c.long_name));
            });
          } else {
            console.warn('No address_components for place:', placeId);
            if (result.formatted_address) {
              const parts = result.formatted_address.split(',');
              if (parts.length >= 2) {
                components.locality = parts[0].trim();
                components.administrative_area_level_1 = parts[parts.length - 2]?.trim() || '';
              }
            }
          }

          resolve({
            name: result.name || 'Unknown Location',
            address: result.formatted_address || '',
            coordinates: result.geometry?.location ? {
              lat: result.geometry.location.lat(),
              lng: result.geometry.location.lng(),
            } : { lat: 0, lng: 0 },
            city: components.locality || components.administrative_area_level_2 || '',
            state: components.administrative_area_level_1 || '',
            placeId,
          });
        }
      );
    } catch (err) {
      console.error('getPlaceDetails error:', err);
      reject(err); 
    }
  });
};

export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          address: {
            name: "Current Location",
            address: `${pos.coords.latitude}, ${pos.coords.longitude}`,
            city: "",
            state: "",
            placeId: ""
          },
          coordinates: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          }
        });
      },
      reject,
      { enableHighAccuracy: true }
    );
  });
};

export const getDistanceMatrix = (origins, destinations) => {
  return new Promise((resolve, reject) => {
    try {
      ensureGoogle();

      const service = new window.google.maps.DistanceMatrixService();
      
      const formatLocations = (locations) => {
        return locations.map(loc => {
          if (loc.lat && loc.lng) {
            return new window.google.maps.LatLng(loc.lat, loc.lng);
          }
          return loc.name || loc.address || loc.city || "";
        });
      };

      const formattedOrigins = formatLocations(origins);
      const formattedDestinations = formatLocations(destinations);

      service.getDistanceMatrix(
        {
          origins: formattedOrigins,
          destinations: formattedDestinations,
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
        },
        (response, status) => {
          if (status === window.google.maps.DistanceMatrixStatus.OK) {
            resolve(response);
          } else {
            reject(new Error(`Distance Matrix API error: ${status}`));
          }
        }
      );
    } catch (err) {
      console.error("Distance Matrix error:", err);
      reject(err);
    }
  });
};

export const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d;
};

const deg2rad = (deg) => {
  return deg * (Math.PI / 180);
};